import React from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Star,
  TrendingUp,
  Filter,
  Calendar,
  DollarSign,
  Zap,
  Loader2,
} from 'lucide-react';
import { PageCard } from './ui/PageCard';
import { PageLoadingSkeleton } from './ui/PageLoadingSkeleton';
import { usePaginatedPages } from '../hooks/usePaginatedPages';

export function AllPagesList() {
  const { pageType } = useParams();
  const currentPageType = 'pages'; // Always show all pages
  
  const { pages, totalCount, hasMore, isLoading, error, isFetching, loadMore, retry } = usePaginatedPages(currentPageType, true);

  const getPageTypeConfig = (type) => {
    switch (type) {
      case 'features':
      case 'feature':
        return {
          title: 'Feature Pages',
          description: 'Discover products by their key features and capabilities',
          icon: Star,
          color: 'from-blue-600 to-indigo-600',
        };
      case 'deals':
      case 'deal':
        return {
          title: 'Deal Pages',
          description: 'Find the best deals and discounts on tech products',
          icon: TrendingUp,
          color: 'from-green-600 to-emerald-600',
        };
      case 'brands':
      case 'brand':
        return {
          title: 'Brand Pages',
          description: 'Explore products from your favorite tech brands',
          icon: Filter,
          color: 'from-purple-600 to-violet-600',
        };
      case 'use-case':
      case 'use_case':
        return {
          title: 'Use Case Pages',
          description: 'Find products perfect for specific needs and use cases',
          icon: Calendar,
          color: 'from-orange-600 to-red-600',
        };
      case 'budget':
        return {
          title: 'Budget Pages',
          description: 'Find the best tech products within your budget range',
          icon: DollarSign,
          color: 'from-green-600 to-teal-600',
        };
      case 'trending':
        return {
          title: 'Trending Pages',
          description: 'Discover the hottest trending tech products',
          icon: Zap,
          color: 'from-pink-600 to-rose-600',
        };
      default:
        return {
          title: 'Pages',
          description: 'Browse our collection of specialized product pages',
          icon: Star,
          color: 'from-gray-600 to-slate-600',
        };
    }
  };

  const { title, description, icon: Icon, color } = getPageTypeConfig(currentPageType);

  if (isLoading) {
    return <PageLoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg max-w-md text-center">
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">Unable to Load Pages</h3>
          <p className="text-red-600 dark:text-red-400 mb-4">
            There was an issue querying data from Supabase.
          </p>
          <p className="text-red-600 dark:text-red-400 text-sm mb-4">Error: {error.message}</p>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
            Please check your Supabase project settings or try again later.
          </p>
          <button
            onClick={retry}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>All Pages - TechieDay</title>
        <meta name="description" content="Browse our complete collection of specialized product pages, deals, guides, and recommendations." />
        <meta property="og:title" content="All Pages - TechieDay" />
        <meta property="og:description" content="Browse our complete collection of specialized product pages, deals, guides, and recommendations." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.techieday.com/pages" />
        <meta property="og:site_name" content="TechieDay" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="All Pages - TechieDay" />
        <meta name="twitter:description" content="Browse our complete collection of specialized product pages, deals, guides, and recommendations." />
      </Helmet>

      <div className="min-h-screen bg-slate-50 dark:bg-[#080d18]">
        <header className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white overflow-hidden">
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
          <div className="relative max-w-7xl mx-auto px-4 py-10 text-center">
            <div className="flex items-center justify-center mb-4">
              <Icon className="w-8 h-8 mr-3 text-blue-200" />
              <h1 className="text-2xl sm:text-3xl font-bold text-white">All Pages</h1>
            </div>
            <p className="text-lg sm:text-xl text-blue-100 max-w-3xl mx-auto">Browse our complete collection of specialized product pages, deals, guides, and recommendations</p>
            <div className="mt-4 inline-flex items-center text-sm text-blue-200 bg-white/10 px-4 py-1.5 rounded-full">
              <span>{totalCount} pages available</span>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-10 relative z-1">
          {pages.length === 0 ? (
            <div className="text-center py-12">
              <Icon className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-slate-700 dark:text-slate-300 mb-2">No Pages Found</h2>
              <p className="text-slate-500 dark:text-slate-400">
                Check back later for new content.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {pages.map((page) => (
                  <PageCard key={page.slug} page={page} />
                ))}
              </div>
              
              {hasMore && (
                <div className="text-center mt-8">
                  <button
                    onClick={loadMore}
                    disabled={isFetching}
                    className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isFetching ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load More Pages'
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
}
